import { productAlias } from './ProductAlias'
import { productFingerprint } from './ProductFingerprint'
import { productNormalizer } from './ProductNormalizer'
import { productValidator } from './ProductValidator'
import type { ProductCatalogSnapshot, ProductModel } from './ProductTypes'

/**
 * ProductCatalog — in-memory catalog keyed by fingerprint with exclusive aliases.
 */
export class ProductCatalog {
  private byFingerprint = new Map<string, ProductModel>()
  private byId = new Map<string, ProductModel>()

  clear(): void {
    this.byFingerprint.clear()
    this.byId.clear()
    productAlias.clear()
  }

  get size(): number {
    return this.byFingerprint.size
  }

  list(): ProductModel[] {
    return [...this.byFingerprint.values()]
  }

  getByFingerprint(fingerprint: string): ProductModel | null {
    return this.byFingerprint.get(productNormalizer.normalize(fingerprint)) ??
      this.byFingerprint.get(fingerprint) ??
      null
  }

  getByAlias(alias: string): ProductModel | null {
    const fp = productAlias.resolve(alias)
    return fp ? this.getByFingerprint(fp) : null
  }

  getById(id: string): ProductModel | null {
    return this.byId.get(id) ?? null
  }

  /**
   * Upserts product. Never creates two equal fingerprints.
   * Merges aliases into the existing product when fingerprint already exists.
   */
  upsert(product: ProductModel): ProductModel {
    const validation = productValidator.validateProduct(product)
    if (!validation.valid) {
      throw new Error(`Produto inválido: ${validation.errors.join('; ')}`)
    }

    const key = product.fingerprint
    const existing = this.byFingerprint.get(key)
    if (existing) {
      const mergedAliases = productAlias.mergeProductAliases(existing, product.aliases)
      const merged: ProductModel = {
        ...existing,
        ...preferNonEmpty(existing, product),
        aliases: mergedAliases,
        fingerprint: existing.fingerprint,
        id: existing.id,
      }
      this.byFingerprint.set(key, merged)
      this.byId.set(merged.id, merged)
      return merged
    }

    // Ensure fingerprint uniqueness across normalized forms
    for (const current of this.byFingerprint.values()) {
      if (productFingerprint.equals(current.fingerprint, product.fingerprint) && current.id !== product.id) {
        return this.upsert({ ...product, fingerprint: current.fingerprint, id: current.id })
      }
    }

    for (const alias of product.aliases) {
      const owned = productAlias.resolve(alias)
      if (owned && owned !== product.fingerprint) {
        throw new Error(`Alias "${alias}" já pertence a ${owned}`)
      }
      productAlias.register(alias, product.fingerprint)
    }

    this.byFingerprint.set(key, product)
    this.byId.set(product.id, product)
    return product
  }

  snapshot(): ProductCatalogSnapshot {
    return {
      products: this.list(),
      aliasCount: productAlias.size(),
      fingerprintCount: this.byFingerprint.size,
    }
  }
}

function preferNonEmpty(base: ProductModel, incoming: ProductModel): Partial<ProductModel> {
  return {
    brand: incoming.brand || base.brand,
    category: incoming.category || base.category,
    line: incoming.line || base.line,
    model: incoming.model || base.model,
    partNumber: incoming.partNumber || base.partNumber,
    capacity: incoming.capacity || base.capacity,
    technology: incoming.technology || base.technology,
    socket: incoming.socket || base.socket,
    generation: incoming.generation || base.generation,
    attributes: { ...base.attributes, ...incoming.attributes },
  }
}

export const productCatalog = new ProductCatalog()
